import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import ListingDetail from "@/pages/ListingDetail";
import Reserver from "@/pages/Reserver";
import Contact from "@/pages/Contact";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import { Loader2 } from "lucide-react";
import { restoreListScroll } from "@/lib/scrollMemory";

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
    </>
  );
}

function ScrollManager() {
  const { pathname, search } = useLocation();
  const navType = useNavigationType();
  const path = pathname + search;

  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  }, []);

  useEffect(() => {
    if (navType !== "POP") {
      window.scrollTo(0, 0);
      return undefined;
    }
    let cancelled = false;
    const apply = () => !cancelled && restoreListScroll(path, { allowY: false });
    if (apply()) return undefined;
    const delays = [80, 200, 450, 900];
    const timers = delays.map((d, i) =>
      setTimeout(() => {
        if (apply()) return;
        if (i === delays.length - 1 && !cancelled) restoreListScroll(path, { allowY: true });
      }, d)
    );
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [path, navType]);

  return null;
}

function AdminGate() {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  return user ? <AdminDashboard /> : <AdminLogin />;
}

function App() {
  return (
    <div className="App">
      <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollManager />
          <Routes>
            <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
            <Route path="/voitures" element={<PublicLayout><Catalog category="cars" /></PublicLayout>} />
            <Route path="/yachts" element={<PublicLayout><Catalog category="yachts" /></PublicLayout>} />
            <Route path="/villas" element={<PublicLayout><Catalog category="properties" /></PublicLayout>} />
            <Route path="/recherche" element={<PublicLayout><Catalog category="all" /></PublicLayout>} />
            <Route path="/annonce/:id" element={<PublicLayout><ListingDetail /></PublicLayout>} />
            <Route path="/reserver" element={<PublicLayout><Reserver /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
            <Route path="/admin" element={<AdminGate />} />
          </Routes>
          <Toaster position="top-center" theme="dark" richColors />
        </BrowserRouter>
      </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
